/**
 * أداة تصحيح منظور المستند (perspective correction) بالكامل داخل المتصفح
 * (على جهاز المستخدم)، بدون أي معالجة على السيرفر - نفس فكرة تطبيقات
 * مسح المستندات (CamScanner وغيرها)، لكن بدون أي خطر على استقرار السيرفر
 * لأن كل الحساب يصير على جهاز المستخدم نفسه.
 *
 * الاستخدام:
 *   const warped = await warpPerspective(imageElement, srcCorners, outWidth, outHeight);
 *   // srcCorners: [{x,y}, {x,y}, {x,y}, {x,y}] بترتيب: أعلى-يسار، أعلى-يمين، أسفل-يمين، أسفل-يسار
 *   // warped: canvas جاهز، حوّله لـ dataURL بـ warped.toDataURL('image/jpeg', 0.9)
 */

// يحسب مصفوفة homography (3x3) من 4 نقاط مصدر إلى 4 نقاط وجهة
function computeHomography(src, dst) {
  // نبني نظام معادلات خطي 8x8 ونحله لإيجاد معاملات التحويل الإسقاطي
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i++) {
    const { x: sx, y: sy } = src[i];
    const { x: dx, y: dy } = dst[i];
    A.push([sx, sy, 1, 0, 0, 0, -sx * dx, -sy * dx]);
    b.push(dx);
    A.push([0, 0, 0, sx, sy, 1, -sx * dy, -sy * dy]);
    b.push(dy);
  }
  const h = solveLinearSystem(A, b); // 8 قيم: h11..h32 (h33 = 1)
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

// حل نظام معادلات خطي بطريقة الحذف الغاوسي (Gaussian elimination)
function solveLinearSystem(A, b) {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let pivotRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[pivotRow][col])) pivotRow = row;
    }
    [M[col], M[pivotRow]] = [M[pivotRow], M[col]];

    const pivot = M[col][col];
    if (Math.abs(pivot) < 1e-12) continue;
    for (let j = col; j <= n; j++) M[col][j] /= pivot;

    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = M[row][col];
      for (let j = col; j <= n; j++) M[row][j] -= factor * M[col][j];
    }
  }
  return M.map((row) => row[n]);
}

function applyHomographyInverse(H, x, y) {
  // نطبّق معكوس homography لإيجاد نقطة المصدر المقابلة لبكسل الوجهة
  // (بدل عكس المصفوفة رياضيًا، نحسب homography من الوجهة للمصدر مباشرة عند الاستخدام)
  const [h11, h12, h13, h21, h22, h23, h31, h32, h33] = H;
  const denom = h31 * x + h32 * y + h33;
  return {
    x: (h11 * x + h12 * y + h13) / denom,
    y: (h21 * x + h22 * y + h23) / denom,
  };
}

/**
 * يصحح منظور صورة مستند: يأخذ 4 زوايا (كما رسمها المستخدم على الصورة الأصلية)
 * ويرجع canvas جديد بحجم outWidth×outHeight فيه المستند مسطحًا ومستقيمًا.
 */
export function warpPerspective(imageElement, srcCorners, outWidth, outHeight) {
  const dstCorners = [
    { x: 0, y: 0 },
    { x: outWidth, y: 0 },
    { x: outWidth, y: outHeight },
    { x: 0, y: outHeight },
  ];

  // homography من نقاط الوجهة (المربع الناتج) إلى نقاط المصدر (الزوايا اللي حددها المستخدم)
  // هذا يخلينا نمشي على بكسلات الوجهة ونسحب قيمتها من المصدر مباشرة (inverse mapping)
  const H = computeHomography(dstCorners, srcCorners);

  const srcCanvas = document.createElement('canvas');
  srcCanvas.width = imageElement.naturalWidth || imageElement.width;
  srcCanvas.height = imageElement.naturalHeight || imageElement.height;
  const srcCtx = srcCanvas.getContext('2d');
  srcCtx.drawImage(imageElement, 0, 0);
  const srcData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);

  const outCanvas = document.createElement('canvas');
  outCanvas.width = outWidth;
  outCanvas.height = outHeight;
  const outCtx = outCanvas.getContext('2d');
  const outData = outCtx.createImageData(outWidth, outHeight);

  const sw = srcCanvas.width;
  const sh = srcCanvas.height;

  for (let y = 0; y < outHeight; y++) {
    for (let x = 0; x < outWidth; x++) {
      const { x: sx, y: sy } = applyHomographyInverse(H, x, y);
      const outIdx = (y * outWidth + x) * 4;

      if (sx >= 0 && sx < sw - 1 && sy >= 0 && sy < sh - 1) {
        // أخذ عينة ثنائية الخطية (bilinear) لجودة أعلى بدل أقرب بكسل فقط
        const x0 = Math.floor(sx), y0 = Math.floor(sy);
        const fx = sx - x0, fy = sy - y0;
        for (let c = 0; c < 4; c++) {
          const p00 = srcData.data[(y0 * sw + x0) * 4 + c];
          const p10 = srcData.data[(y0 * sw + x0 + 1) * 4 + c];
          const p01 = srcData.data[((y0 + 1) * sw + x0) * 4 + c];
          const p11 = srcData.data[((y0 + 1) * sw + x0 + 1) * 4 + c];
          const top = p00 * (1 - fx) + p10 * fx;
          const bottom = p01 * (1 - fx) + p11 * fx;
          outData.data[outIdx + c] = top * (1 - fy) + bottom * fy;
        }
      } else {
        outData.data[outIdx] = 255;
        outData.data[outIdx + 1] = 255;
        outData.data[outIdx + 2] = 255;
        outData.data[outIdx + 3] = 255;
      }
    }
  }

  outCtx.putImageData(outData, 0, 0);
  return outCanvas;
}

/** يطبّق تحسين بسيط للسطوع/التباين على canvas (بدون مكتبات خارجية) */
export function enhanceCanvas(canvas) {
  const ctx = canvas.getContext('2d');
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imgData.data;
  const contrast = 1.15;
  const brightness = 12;
  for (let i = 0; i < data.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      let v = data[i + c];
      v = (v - 128) * contrast + 128 + brightness;
      data[i + c] = Math.max(0, Math.min(255, v));
    }
  }
  ctx.putImageData(imgData, 0, 0);
  return canvas;
}

/** يقدّر أبعاد إخراج معقولة (نسبة عرض/طول) بناءً على متوسط أطوال أضلاع الرباعي المحدد */
export function estimateOutputSize(corners, maxDim = 1400) {
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const widthTop = dist(corners[0], corners[1]);
  const widthBottom = dist(corners[3], corners[2]);
  const heightLeft = dist(corners[0], corners[3]);
  const heightRight = dist(corners[1], corners[2]);
  const width = Math.max(widthTop, widthBottom);
  const height = Math.max(heightLeft, heightRight);
  const scale = maxDim / Math.max(width, height, 1);
  return {
    width: Math.round(width * Math.min(scale, 1.5)),
    height: Math.round(height * Math.min(scale, 1.5)),
  };
}
